import { AudioPresets, type AudioCaptureOptions } from 'livekit-client';

import type { LinkSample } from './codec';

/**
 * Качество звука.
 *
 * ── Почему режимов два, а не три ────────────────────────────────────
 *
 * Здесь было «Высокое качество»: 128 кбит/с стерео с выключенным
 * шумоподавлением, задуманное под музыку и демонстрацию звука. На
 * практике оно приносило только вред.
 *
 *   · Без шумоподавления в общий канал шёл весь фон комнаты —
 *     кондиционер, клавиатура, коридор. На встрече с десятью
 *     участниками это складывается и заглушает говорящего;
 *   · стерео с монофонического микрофона (а такие почти все) не даёт
 *     ничего, кроме удвоенного трафика;
 *   · 128 кбит/с на человека при большой конференции — это уже заметный
 *     кусок канала, потраченный на то, чего никто не слышит: речь
 *     полностью укладывается в 48.
 *
 * Осталось два честных режима: «Автоматически» подстраивается под связь,
 * «Речь» держит экономный битрейт всегда. Обработка микрофона в обоих
 * одинаковая и полная.
 *
 * ── Что именно включено в обработке ─────────────────────────────────
 *
 *   · **автоусиление** (AGC) вытягивает голос до нормального уровня.
 *     Встроенные микрофоны ноутбуков отдают очень тихий сигнал, и без
 *     AGC высокий битрейт просто аккуратно передаёт тишину;
 *   · **эхоподавление** (AEC) убирает из микрофона то, что играет
 *     в динамиках. Без него собеседники слышат собственный голос
 *     с задержкой — разговор разваливается;
 *   · **шумоподавление** (NS) выключается только вручную, отдельным
 *     переключателем, и только если человек знает, зачем ему это.
 *
 * Используется штатная обработка WebRTC (Chrome, Safari, Firefox, webview
 * Telegram). Внешние нейросетевые фильтры вроде Krisp не подключаем: они
 * требуют облачной подписки LiveKit и заметно грузят процессор.
 */

export type AudioMode = 'auto' | 'speech';

export const AUDIO_MODE_LABEL: Record<AudioMode, string> = {
  auto: 'Автоматически',
  speech: 'Речь',
};

export const AUDIO_MODE_NOTE: Record<AudioMode, string> = {
  auto: 'Битрейт подстраивается под связь — обычный режим',
  speech: 'Экономный битрейт всегда — для слабой связи и больших встреч',
};

/** Ступени битрейта для автоматического режима, от худшей связи к лучшей. */
const AUTO_LADDER = [
  AudioPresets.telephone, // 12 кбит/с
  AudioPresets.speech, //    24 кбит/с
  AudioPresets.music, //     48 кбит/с
] as const;

const START_RUNG = 1; // speech

export function audioPresetFor(mode: AudioMode) {
  if (mode === 'speech') return AudioPresets.speech;
  return AUTO_LADDER[START_RUNG];
}

/**
 * Обработка микрофона.
 *
 * Эхоподавление и автоусиление включены всегда: без них человека либо не
 * слышно, либо слышно вместе с эхом собственных динамиков. Шумоподавление
 * — единственное, что участник может выключить сам.
 */
function processing(noiseSuppression: boolean) {
  return {
    echoCancellation: true,
    autoGainControl: true,
    noiseSuppression,
  };
}

/** Ограничения захвата микрофона. */
export function captureOptions(
  noiseSuppression: boolean,
  deviceId?: string,
): AudioCaptureOptions {
  return {
    deviceId: deviceId || undefined,
    ...processing(noiseSuppression),
    // channelCount намеренно не задаём. Подавляющее большинство гарнитур
    // и встроенных микрофонов — монофонические, и запрос стерео приводил
    // к отказу getUserMedia. Стерео и так не даёт ничего для речи, а цена
    // ошибки — полностью пропавший звук у собеседников.
  };
}

/**
 * Ограничения, которые можно менять у живой дорожки.
 *
 * Отделены от `captureOptions` намеренно: `deviceId` менять на лету нельзя,
 * для этого нужен перезахват.
 */
export function liveConstraints(noiseSuppression: boolean): MediaTrackConstraints {
  return processing(noiseSuppression);
}

// ── Автоматический подбор битрейта ────────────────────────────────────

/** Проб подряд, после которых меняем ступень. */
const DOWN_STREAK = 4;
const UP_STREAK = 8;
const COOLDOWN_MS = 20_000;

export interface AudioAdvice {
  maxBitrate: number;
  label: string;
}

/**
 * Подбирает битрейт звука по той же статистике, что и советчик кодека.
 *
 * В отличие от видео, менять битрейт звука дёшево: он применяется через
 * `RTCRtpSender.setParameters` без перепубликации дорожки, поэтому пороги
 * мягче и пауза короче — разрыва в звуке не возникает.
 */
export class AudioAdvisor {
  private rung = START_RUNG;
  private bad = 0;
  private good = 0;
  private lastChangeAt: number | null = null;

  get bitrate(): number {
    return AUTO_LADDER[this.rung].maxBitrate!;
  }

  consider(sample: LinkSample, now = Date.now()): AudioAdvice | null {
    // Звук страдает от потерь и узкого канала; нехватка процессора
    // на него почти не влияет, поэтому 'cpu' здесь не учитываем.
    const struggling = sample.loss > 0.04 || (sample.bitrate > 0 && sample.bitrate < 400_000);
    const thriving = sample.loss < 0.01 && sample.bitrate > 1_500_000;

    this.bad = struggling ? this.bad + 1 : 0;
    this.good = thriving ? this.good + 1 : 0;

    if (this.lastChangeAt !== null && now - this.lastChangeAt < COOLDOWN_MS) return null;

    if (this.bad >= DOWN_STREAK && this.rung > 0) {
      return this.move(this.rung - 1, now, 'связь слабая — снижаем битрейт звука');
    }

    if (this.good >= UP_STREAK && this.rung < AUTO_LADDER.length - 1) {
      return this.move(this.rung + 1, now, 'связь стабильна — повышаем качество звука');
    }

    return null;
  }

  private move(rung: number, now: number, label: string): AudioAdvice {
    this.rung = rung;
    this.lastChangeAt = now;
    this.bad = 0;
    this.good = 0;
    return { maxBitrate: this.bitrate, label };
  }
}

/** Человекочитаемый битрейт: «48 кбит/с». */
export const formatAudioBitrate = (bps: number) => `${Math.round(bps / 1000)} кбит/с`;

/**
 * Приводит сохранённое значение к живому режиму.
 *
 * В настройках браузера у тех, кто уже пользовался сервисом, лежит `hq` —
 * режима с таким именем больше нет. Без этой проверки такой человек попал
 * бы в режим, для которого нет ни подписи, ни битрейта.
 */
export function normalizeAudioMode(value: unknown): AudioMode {
  return value === 'speech' ? 'speech' : 'auto';
}
